import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { http } from "../../lib/api";
import { Reveal, Kicker, SectionTitle } from "./Reveal";
import { Logo } from "./Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

export const Faq = () => {
  const { t } = useLang();
  return (
    <section id="faq" className="border-t border-[#073B4C]/10 bg-[#FBF7EF] py-24 md:py-32" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <Kicker>{t.faq.kicker}</Kicker>
          <SectionTitle className="mt-6">{t.faq.title}</SectionTitle>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <Accordion type="single" collapsible defaultValue="q0">
            {t.faq.items.map((it, i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-[#073B4C]/15">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-2xl font-normal hover:no-underline md:text-[1.7rem]">{it.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-relaxed text-[#3F5F68]">{it.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export const Newsletter = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error(t.news.bad); return; }
    setBusy(true);
    try { await http.post("/newsletter", { email, lang }); toast.success(t.news.ok); setEmail(""); }
    catch { toast.error(t.news.bad); }
    setBusy(false);
  };
  return (
    <section className="relative overflow-hidden bg-[#073B4C] py-24 text-[#FBF7EF] md:py-32" data-testid="newsletter-section">
      <img src="/img/palms.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#073B4C]/60" />
      <div className="relative mx-auto grid max-w-7xl items-end gap-8 px-5 md:px-10 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-display text-4xl leading-tight md:text-5xl">{t.news.title}</h2>
          <p className="mt-3 max-w-md text-[#FBF7EF]/85">{t.news.body}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="flex border-b-2 border-[#FBF7EF]/50 focus-within:border-[#7ED9D2]" data-testid="newsletter-form">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.news.placeholder} type="email" data-testid="newsletter-email-input"
              className="min-w-0 flex-1 bg-transparent py-4 text-lg text-[#FBF7EF] outline-none placeholder:text-[#FBF7EF]/60" />
            <button disabled={busy} data-testid="newsletter-submit" className="group flex items-center gap-2 font-semibold text-[#7ED9D2]">
              {t.news.submit}<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#073B4C] text-[#FBF7EF]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-16 md:px-10">
        <p className="font-display text-[19vw] leading-[0.8] text-[#7ED9D2]/90 md:text-[13vw]">Mareia</p>
        <div className="mt-10 grid gap-6 border-t border-[#FBF7EF]/15 pt-8 text-sm text-[#FBF7EF]/75 md:grid-cols-3">
          <Logo light />
          <div className="space-y-2">
            <p>{t.footer.note}</p>
            <p>{t.footer.disclaimer}</p>
          </div>
          <div className="md:text-right">
            <p>© {new Date().getFullYear()} Mareia</p>
            <Link to="/admin" className="mt-2 inline-block underline-offset-4 hover:underline" data-testid="footer-admin-link">{t.footer.admin}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
